import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Maps-Key wird serverseitig ermittelt und am <html>-Element übergeben
// (includes/MapsRuntime.php). Er läuft ab und steht deshalb nie im Build.
function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }
  return document.documentElement.dataset.mapsKey || "";
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function validLat(v) {
  return typeof v === "number" && isFinite(v) && v >= -90 && v <= 90;
}

function validLng(v) {
  return typeof v === "number" && isFinite(v) && v >= -180 && v <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.autocompleteSource = null;
    this.autocompleteSeq = 0;
    this.reverseSource = null;
    this.reverseSeq = 0;
  }

  cancelAutocomplete() {
    // Jede laufende oder verspätete Antwort wird damit ungültig.
    this.autocompleteSeq++;
    if (this.autocompleteSource != null) {
      this.autocompleteSource.cancel();
      this.autocompleteSource = null;
    }
  }

  cancelReverse() {
    this.reverseSeq++;
    if (this.reverseSource != null) {
      this.reverseSource.cancel();
      this.reverseSource = null;
    }
  }

  cancelAll() {
    this.cancelAutocomplete();
    this.cancelReverse();
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.label) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature der Screenwork-Geocoder-API in das bisherige
  // Adressformat um. Das Feature selbst wird nicht verändert.
  _geoaddress(l) {
    if (!l || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validLat(lat) || !validLng(lng)) {
      return null;
    }

    const p = l.properties || {};
    const type = str(p.type);
    const name = str(p.name);
    const geoaddress = {};

    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);
    let label = null;

    if (type) {
      switch (type) {
        case "house":
          // Gebäude/POI: Name nur als Beschriftung, nicht als Ort.
          if (name && name !== street) {
            label = name;
          }
          break;
        case "street":
          street = street || name;
          break;
        case "locality":
        case "district":
          if (name && name !== city) {
            if (city) {
              district = name;
            } else {
              label = name;
            }
          }
          break;
        case "city":
          city = city || name;
          break;
        default:
          // county, state, country, other …
          if (name && name !== city && name !== district) {
            label = name;
          }
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type
      if (p.osm_key == "highway") {
        street = street || name;
      } else if (p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb")) {
        if (name && name !== city) {
          if (city) {
            district = district || name;
          } else {
            label = name;
          }
        }
      } else if (p.osm_key == "place" && !city) {
        city = name;
      } else if (name && name !== city && name !== street) {
        label = name;
      }
    }

    if (street) {
      geoaddress["street"] = street;
      if (str(p.housenumber)) {
        geoaddress["number"] = p.housenumber;
      }
    }

    if (str(p.postcode)) {
      geoaddress["zip"] = p.postcode;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    if (label) {
      geoaddress["label"] = label;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  // Liefert ein Array, oder null, wenn die Anfrage abgebrochen oder durch eine
  // neuere ersetzt wurde. Das Promise wird immer erfüllt oder abgelehnt.
  autocomplete(q) {
    this.cancelAutocomplete();
    const seq = this.autocompleteSeq;

    const key = mapsKey();
    if (!key || typeof q !== "string" || q.trim() === "") {
      return Promise.resolve([]);
    }

    const params = new URLSearchParams();
    params.append("q", q.trim());
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");
    params.append("key", key);

    const source = this.axios.CancelToken.source();
    this.autocompleteSource = source;

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.autocompleteSeq) {
        return null;
      }

      const results = [];
      if (res.data && Array.isArray(res.data.features)) {
        res.data.features.forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });
      }

      return results;
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.autocompleteSeq) {
        return null;
      }
      throw thrown;
    }).finally(() => {
      if (this.autocompleteSource === source) {
        this.autocompleteSource = null;
      }
    });
  }

  // Liefert die Adresse oder null (kein Key, ungültige Koordinaten, Fehler,
  // keine Treffer oder durch eine neuere Anfrage ersetzt).
  reverse(lat, lng) {
    this.cancelReverse();
    const seq = this.reverseSeq;

    lat = Number(lat);
    lng = Number(lng);

    const key = mapsKey();
    if (!key || !validLat(lat) || !validLng(lng)) {
      return Promise.resolve(null);
    }

    const source = this.axios.CancelToken.source();
    this.reverseSource = source;

    return this.axios.get(REVERSE_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: key
      }
    }).then(res => {
      if (seq !== this.reverseSeq) {
        return null;
      }
      if (res.data && Array.isArray(res.data.features) && res.data.features.length > 0) {
        return this._geoaddress(res.data.features[0]);
      }
      return null;
    }).catch(thrown => {
      if (!this.axios.isCancel(thrown)) {
        console.log(thrown);
      }
      return null;
    }).finally(() => {
      if (this.reverseSource === source) {
        this.reverseSource = null;
      }
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
